import { Button, Dialog, Spinner } from '@risksmart-app/atomic-ui';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  Appetite_Model_Enum,
  Recalculate_Appetites_Error_Code_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useModulesStore } from 'src/context/moduleContext';
import { useRecalculateAppetites } from 'src/hooks/mutations/aggregation/useRecalculateAppetites';
import { useAggregation } from 'src/hooks/useAggregation';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

export const AppetiteCascadingSettings: FC = () => {
  const {
    control,
    formState: { isDirty: isFormDirty },
  } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');
  const isCustomerSupport = useIsCustomerSupport();
  const { addNotification } = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Read directly from aggregation_org (same source the lambda checks) so the gate can't drift from persisted state.
  // Gate on both store-level dirtiness (module toggles via useModulesStore.toggle) and RHF form dirtiness (this select / json editor).
  const { appetiteAggregation, loading: aggregationLoading } = useAggregation();
  const isStoreDirty = useModulesStore((state) => state.isDirty);
  const cascadeEnabled =
    !isStoreDirty &&
    !isFormDirty &&
    !aggregationLoading &&
    appetiteAggregation === Appetite_Model_Enum.TopDownCascade;

  const { recalculateAppetites, loading: recalculating } =
    useRecalculateAppetites();

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    try {
      const { errorCode } = await recalculateAppetites();
      if (
        errorCode === Recalculate_Appetites_Error_Code_Enum.CascadeNotConfigured
      ) {
        addNotification({
          type: 'error',
          content: t('RecalculateAppetiteCascade_error_cascade_not_configured'),
        });

        return;
      }
      if (
        errorCode === Recalculate_Appetites_Error_Code_Enum.CascadeNotEnabled
      ) {
        addNotification({
          type: 'error',
          content: t('RecalculateAppetiteCascade_error_cascade_not_enabled'),
        });

        return;
      }
      addNotification({
        type: 'success',
        content: t('RecalculateAppetiteCascade_success'),
      });
    } catch {
      addNotification({
        type: 'error',
        content: t('RecalculateAppetiteCascade_error'),
      });
    }
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <ControlledSelect
        testId={'appetiteCascadingModel'}
        control={control}
        name={'AppetiteCascadingModel'}
        label={t('AppetiteCascadingModel')}
        options={[
          {
            label: t('AppetiteCascadingModels.default'),
            value: Appetite_Model_Enum.Default,
          },
          {
            label: t('AppetiteCascadingModels.top_down_cascade'),
            value: Appetite_Model_Enum.TopDownCascade,
          },
        ]}
      />
      <ControlledJsonEditor
        control={control}
        name={'AppetiteCascadingModelConfig'}
        label={t('AppetiteCascadingModelConfig')}
      />
      {isCustomerSupport && cascadeEnabled && (
        <>
          <Button
            data-testid={'recalculate-appetite-cascade-button'}
            variant={'neutral'}
            style={'outline'}
            className={'w-fit'}
            onClick={() => setShowConfirmModal(true)}
            disabled={recalculating}
          >
            {recalculating && <Spinner />}
            {t('RecalculateAppetiteCascade')}
          </Button>
          <Dialog
            open={showConfirmModal}
            onOpenChange={(open) => {
              if (!open) {
                setShowConfirmModal(false);
              }
            }}
            size={'sm'}
          >
            <Dialog.Header
              title={t('RecalculateAppetiteCascade_confirm_header')}
            />
            <Dialog.Body>
              <div data-testid={'recalculate-appetite-cascade-modal'}>
                {t('RecalculateAppetiteCascade_confirm_body')}
              </div>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                variant={'neutral'}
                style={'outline'}
                onClick={() => setShowConfirmModal(false)}
              >
                {ct('cancel')}
              </Button>
              <Button onClick={handleRecalculate}>
                {t('RecalculateAppetiteCascade_confirm_button')}
              </Button>
            </Dialog.Footer>
          </Dialog>
        </>
      )}
    </div>
  );
};
